/** MSC Super Admin: review agents' IRS EIN documents (SS-4 / CP 575) before their business W-9 counts as verified. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, PageHeader, StatusPill, btnPrimary, btnSmall, errorText, inputCls } from '../../components/finance/financeUi';
import { openPdf } from '../../components/agreements/agreementShared';

const TABS = [['PENDING_REVIEW', 'Waiting for review'], ['APPROVED', 'Approved'], ['REJECTED', 'Rejected']];

function ReviewRow({ r, onDecided }) {
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const d = r.ein_document;
  const decide = async (action) => {
    try {
      await api.request(`/admin/ein-reviews/${r.user_id}/${action}`, { method: 'POST', body: JSON.stringify({ reason }) });
      toast.success(action === 'approve' ? `${r.agent_name}'s EIN document approved` : 'Rejected — the agent was asked to upload a new copy');
      onDecided();
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="space-y-2 py-4" data-testid={`ein-review-${r.user_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="text-sm">
          <p className="font-semibold">{r.agent_name} <span className="font-normal text-muted-foreground">· {r.email}</span></p>
          <p>W-9: <b>{r.business_name || r.legal_name}</b> · {r.entity_type} · EIN ending <span className="font-mono">{r.tin_last4}</span></p>
          <p className="text-xs text-muted-foreground">{d.filename} · uploaded {new Date(d.uploaded_at).toLocaleString()}{d.reviewed_at ? ` · reviewed ${new Date(d.reviewed_at).toLocaleString()}` : ''}</p>
          {d.review_reason && <p className="text-xs text-red-700">Reason: {d.review_reason}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill value={d.status} />
          <button type="button" className={btnSmall} onClick={() => openPdf(`/admin/ein-reviews/${r.user_id}/document`).catch((e) => toast.error(errorText(e)))} data-testid={`ein-view-${r.user_id}`}>View document</button>
          {d.status === 'PENDING_REVIEW' && <>
            <button type="button" className={btnPrimary} onClick={() => decide('approve')} data-testid={`ein-approve-${r.user_id}`}>Approve</button>
            <button type="button" className={btnSmall} onClick={() => setRejecting((v) => !v)} data-testid={`ein-reject-toggle-${r.user_id}`}>Reject</button>
          </>}
        </div>
      </div>
      {rejecting && <div className="flex flex-wrap gap-2">
        <input className={`${inputCls} max-w-md`} placeholder="Why? e.g. business name doesn't match the W-9" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`ein-reject-reason-${r.user_id}`} />
        <button type="button" className={btnSmall} disabled={!reason.trim()} onClick={() => decide('reject')} data-testid={`ein-reject-confirm-${r.user_id}`}>Send rejection</button>
      </div>}
    </div>
  );
}

export default function EinReviewsPage() {
  const [tab, setTab] = useState('PENDING_REVIEW');
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.request(`/admin/ein-reviews?status=${tab}`).then((d) => setRows(d.reviews)).catch((e) => toast.error(errorText(e))), [tab]);
  useEffect(() => { setRows(null); load(); }, [load]);
  return (
    <div className="space-y-6 pb-24" data-testid="ein-reviews-page">
      <PageHeader eyebrow="Activation" title="EIN document review" subtitle="A business W-9 counts as verified only after MSC approves the IRS EIN document." />
      <div className="flex flex-wrap gap-2">{TABS.map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} className={`rounded-full border px-4 py-1.5 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'}`} data-testid={`ein-tab-${k}`}>{l}</button>)}</div>
      <Card title={TABS.find(([k]) => k === tab)[1]} testid="ein-reviews-card">
        <div className="divide-y px-5">{!rows ? <p className="py-4 text-sm text-muted-foreground">Loading…</p> : !rows.length ? <p className="py-4 text-sm text-muted-foreground" data-testid="ein-reviews-empty">Nothing here.</p> : rows.map((r) => <ReviewRow key={r.user_id} r={r} onDecided={load} />)}</div>
      </Card>
    </div>
  );
}
